// The kiosk marker, kept in a leaf module on purpose: accSession.js (eager, imported by the
// command client) must read it, and the sign-in code that writes it imports accSession, so a
// flag that lived beside the sign-in would be an import cycle.
//
// localStorage rather than a cookie: it is a device fact ("this iPad signed in by PIN"), not a
// credential, and it has to be readable before any request goes out.

import Cookies from 'js-cookie';

export const KIOSK_FLAG = 'bbvr_kiosk';
const NOTICE_KEY = 'bbvr_kiosk_notice';

/** Fired when the device was signed out of a kiosk session by something other than the trainer. */
export const KIOSK_SIGNED_OUT_EVENT = 'kiosk-signed-out';

export const KIOSK_SIGNED_OUT_NOTICE = 'Signed out. Tap your name to sign in.';
export const KIOSK_OFF_NOTICE = 'Kiosk is off. Ask a manager.';

const store = () => {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch (e) {
        return null;
    }
};

const cookieSaysKiosk = () => {
    try {
        return JSON.parse(Cookies.get('user') || 'null')?.kiosk === true;
    } catch (e) {
        return false;
    }
};

const flagSaysKiosk = () => {
    try {
        return store()?.getItem(KIOSK_FLAG) === '1';
    } catch (e) {
        return false;
    }
};

/**
 * The one answer to "is this a PIN session". Either marker is enough: a cleared localStorage
 * must not turn a live kiosk session into a Google one, and a lapsed cookie with the flag still
 * set is still a kiosk device that belongs on the name grid.
 */
export const isKioskSession = () => cookieSaysKiosk() || flagSaysKiosk();

export const setKioskFlag = () => {
    try {
        store()?.setItem(KIOSK_FLAG, '1');
    } catch (e) {
        /* storage blocked: the cookies still carry the session */
    }
};

export const clearKioskFlag = () => {
    try {
        store()?.removeItem(KIOSK_FLAG);
    } catch (e) {
        /* nothing to clear */
    }
};

/** One line the kiosk page shows once, then forgets. */
export const setKioskNotice = (message) => {
    try {
        window.sessionStorage.setItem(NOTICE_KEY, message);
    } catch (e) {
        /* the page just shows no line */
    }
};

export const takeKioskNotice = () => {
    try {
        const message = window.sessionStorage.getItem(NOTICE_KEY) || '';
        window.sessionStorage.removeItem(NOTICE_KEY);
        return message;
    } catch (e) {
        return '';
    }
};

/**
 * Drops the kiosk identity (the `user` cookie and the flag) and tells the app. The caller
 * clears `acc_session`: that cookie belongs to accSession.js, which imports this module.
 */
export const dropKioskIdentity = (notice) => {
    Cookies.remove('user');
    clearKioskFlag();
    if (notice) setKioskNotice(notice);
    try {
        window.dispatchEvent(new CustomEvent(KIOSK_SIGNED_OUT_EVENT));
    } catch (e) {
        /* no window to tell */
    }
};
